import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import Navbar from "../../Components/Navbar";
import "./Details.css";
import { Link } from "react-router-dom";
import { NAV_ITEMS as navigation, usePostbackAlert, AlertDot, useSidebarDock, SidebarDockButton } from "../navigation";
import { ReactComponent as InstaCureLogo } from "Assets/InstacureLightLogo.svg";

export default function DetailLayout({ children, leftColumn }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const postbackAlert = usePostbackAlert();
  const { sidebarDocked, toggleSidebar } = useSidebarDock();

  return (
    <>
      <div className="min-h-screen flex">
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-40 lg:hidden"
            onClose={setMobileMenuOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white focus:outline-none">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-4">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="pt-5 pb-4">
                  <div className="flex-shrink-0 flex items-center px-4">
                    <InstaCureLogo className="h-8 w-auto" />
                  </div>
                  <nav aria-label="Sidebar" className="mt-5">
                    <div className="flex flex-col gap-2 pl-2">
                      {navigation.map((item) => (
                        <Link to={item.path}>
                          {" "}
                          <div
                            key={item.name}
                            className="group p-2 rounded-md flex items-center gap-3 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          >
                            <span className="relative">
                              <item.icon
                                className="h-6 w-6 text-white group-hover:text-gray-500"
                                aria-hidden="true"
                              />
                              {item.name === "Conversions" && postbackAlert && <AlertDot />}
                            </span>
                            {item.name}
                          </div>{" "}
                        </Link>
                      ))}
                    </div>
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Sidebar dock toggle (desktop only) — same control and same stored
            preference as MainLayout, so it does not reappear when you open a
            patient detail page. */}
        <SidebarDockButton sidebarDocked={sidebarDocked} toggleSidebar={toggleSidebar} />

        {/* Static sidebar for desktop */}
        <div className={sidebarDocked ? "hidden" : "hidden lg:flex lg:flex-shrink-0"}>
          <div className="flex flex-col w-28">
            <div className="flex-1 flex flex-col fixed h-full bg-blue-chill w-28 overflow-y-auto">
              <div className="flex-1">
                {/* Sized to match MainLayout: the rail holds the same 8 items,
                    so a taller header here was the only reason the nav
                    overflowed and scrolled on detail pages. */}
                <div className="bg-blue-chill py-3 flex items-center justify-center">
                  <InstaCureLogo className="h-10 w-auto" />
                </div>
                <nav
                  aria-label="Sidebar"
                  className="flex flex-col items-center py-2"
                >
                  {navigation.map((item) => (
                    <Link to={item.path}>
                      <div
                        key={item.name}
                        className="flex flex-col py-4 items-center gap-2 text-indigo-200 hover:bg-blue-light w-28 rounded-none"
                      >
                        {" "}
                        <span className="relative">
                          <item.icon
                            className="h-6 w-6 text-white"
                            aria-hidden="true"
                          />
                          {item.name === "Conversions" && postbackAlert && <AlertDot />}
                        </span>
                        <span className="flex text-white w-full items-center justify-center text-center text-sm leading-tight">
                          {item.name}
                        </span>
                      </div>
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <Navbar onOpenSidebar={() => setMobileMenuOpen(true)} />
          <main className="flex-1 flex overflow-hidden detail-layout-main">
            {/* Primary column */}
            <section
              aria-labelledby="primary-heading"
              className="min-w-0 flex-1  flex flex-col lg:order-last detail-layout-section"
            >
              {children}
              {/* Your content */}
            </section>

            {/* Secondary column (hidden on smaller screens) */}
            <aside className="hidden md:block lg:flex-shrink-0 md:order-first detail-layout-aside">
              <div className="h-full relative flex flex-col w-48 xl:w-56 border-r border-gray-200 bg-white overflow-y-auto">
                {/* Your content */}
                {leftColumn}
              </div>
            </aside>
          </main>
        </div>
      </div>
    </>
  );
}
